// The music source: the provider contract an adaptive-music feature drives (rule 7: whatever may be replaced sits
// behind a contract). A generative director (@rpg/audio's), a stem player or a generated score can each be one. The
// feature asks for moods and states and turns an intensity dial; the source decides how its music answers, on its own
// bar grid. Everything takes `now` (seconds on the audio clock): a source never reads a clock of its own, so a test
// drives it with a fake one.

/** The music source contract's version. */
export const MUSIC_SOURCE_VERSION = "1.0.0" as const;

/** A musical state: what the game is doing, as music hears it. The named ones are the common set; a game may add its
 *  own names, which a source may ignore (its capabilities say which it knows). */
export type MusicStateName = "silence" | "explore" | "tension" | "combat" | "victory" | (string & {});

/** Where a change lands: at once, on the next beat, the next bar line, or the next phrase (a source's own unit, often
 *  two or four bars). */
export type MusicTransitionPoint = "now" | "beat" | "bar" | "phrase";

/** How a change should land. A source that can't land on `at` uses the nearest later point it has. */
export interface MusicTransition {
  at: MusicTransitionPoint;
  /** Crossfade seconds, where the source fades. */
  fade?: number;
}

/** The source's bar grid now. */
export interface BarGrid {
  /** Seconds per bar. */
  barLength: number;
  beatsPerBar: number;
  /** When the next bar line falls (seconds), or null before `start`. */
  nextBar: number | null;
  /** Bars since the current mood started. */
  bar: number;
}

/** What a source can do. */
export interface MusicSourceCapabilities {
  /** The moods it can play (its ids: "valley", "night", "town"). */
  moods: readonly string[];
  /** The states it answers to. */
  states: readonly MusicStateName[];
  /** The transition points it can land on. */
  transitions: readonly MusicTransitionPoint[];
  /** How it follows the intensity dial: smoothly, in steps (layers in or out at thresholds), or not at all. */
  intensity: "continuous" | "stepped" | "none";
}

/** What a source reports. */
export interface MusicSourceState {
  /** The mood playing (or the one it is moving to, once the change has landed). */
  mood: string;
  /** The state it is in. */
  state: MusicStateName;
  /** The intensity it was last given, 0..1. */
  intensity: number;
  grid: BarGrid | null;
}

/** A music source. */
export interface MusicSource {
  /** The contract version it implements. */
  readonly contract: string;
  capabilities(): MusicSourceCapabilities;
  /** Start the music at `now` (the first bar line). */
  start(now: number): void;
  /** Move to a mood. An unknown mood is ignored (and the state says so). */
  setMood(mood: string, now: number, transition?: MusicTransition): void;
  /** Move to a state. An unknown state is ignored. */
  setState(state: MusicStateName, now: number, transition?: MusicTransition): void;
  /** Turn the intensity dial, 0 (sparse, calm) to 1 (full, urgent); values outside are clamped. */
  setIntensity(level: number, now: number): void;
  /** Schedule what falls before `now` plus the source's look-ahead. Call every frame. */
  update(now: number): void;
  /** The bar grid, or null before `start`. */
  grid(): BarGrid | null;
  state(): MusicSourceState;
}

/** Clamp an intensity to 0..1 (NaN is 0). */
export function clampIntensity(level: number): number {
  return Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : level === Infinity ? 1 : 0;
}

/**
 * The conformance check any implementation can run in a unit test: drives `source` on a fake clock and returns what
 * broke the contract (empty: it conforms). It checks the capabilities are non-empty, the grid exists after `start`,
 * every mood and state it claims can be reached and is reported, the intensity is clamped, and unknown moods and
 * states are ignored rather than thrown.
 */
export function checkMusicSource(source: MusicSource): string[] {
  const out: string[] = [];
  const caps = source.capabilities();
  if (!caps.moods.length) out.push("capabilities: no moods");
  if (!caps.states.length) out.push("capabilities: no states");
  if (!caps.transitions.length) out.push("capabilities: no transition points");
  if (source.grid() !== null) out.push("grid: not null before start");
  let now = 0;
  source.start(now);
  const grid = source.grid();
  if (!grid || !(grid.barLength > 0) || !(grid.beatsPerBar > 0)) {
    out.push(`grid: after start, expected a bar length and beats per bar, got ${JSON.stringify(grid)}`);
    return out;
  }
  const run = (bars: number): void => {
    const end = now + bars * Math.max(source.grid()?.barLength ?? grid.barLength, 0.01);
    while (now < end) {
      now += 0.05;
      source.update(now);
    }
  };
  const settle = (): void => run(caps.transitions.includes("phrase") ? 9 : 3);
  for (const mood of caps.moods) {
    source.setMood(mood, now);
    settle();
    if (source.state().mood !== mood) out.push(`mood "${mood}": state reports "${source.state().mood}" after it should have landed`);
  }
  for (const state of caps.states) {
    source.setState(state, now);
    settle();
    if (source.state().state !== state) out.push(`state "${state}": state reports "${source.state().state}" after it should have landed`);
  }
  const before = source.state();
  try {
    source.setMood("__no_such_mood__", now);
    source.setState("__no_such_state__", now);
    settle();
    if (source.state().mood !== before.mood) out.push("an unknown mood changed the mood");
    if (source.state().state !== before.state) out.push("an unknown state changed the state");
  } catch (e) {
    out.push(`an unknown mood or state threw: ${e instanceof Error ? e.message : String(e)}`);
  }
  source.setIntensity(2, now);
  if (source.state().intensity !== 1) out.push(`intensity 2 reported as ${source.state().intensity}, expected 1 (clamped)`);
  source.setIntensity(-1, now);
  if (source.state().intensity !== 0) out.push(`intensity -1 reported as ${source.state().intensity}, expected 0 (clamped)`);
  source.setIntensity(0.5, now);
  if (source.state().intensity !== 0.5) out.push(`intensity 0.5 reported as ${source.state().intensity}`);
  const g = source.grid();
  if (g && g.nextBar !== null && g.nextBar < now - 1e-6) out.push(`grid: next bar ${g.nextBar} is behind now ${now}`);
  return out;
}
